/**
 * Activity event tracker — queue, dedup, batch, flush.
 *
 * Uses raw fetch (NOT tenantServer.call) to avoid 401-logout side effects.
 * All errors are silently swallowed — tracking must never break the app.
 */

import { useAuthStore } from '@/lib/store/authStore';
import { getSessionId } from './session';
import type { ActivityEvent, TrackEventInput } from './types';

// --- Constants ---
const FLUSH_INTERVAL_MS = 30_000; // 30 seconds
const MAX_QUEUE_SIZE = 100;
const MAX_LOCALSTORAGE_SIZE = 1000;
const DEDUP_WINDOW_MS = 2_000; // 2 seconds
const LS_KEY = 'zvg_activity_queue';

// --- State ---
let queue: ActivityEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let lastPageView: { path: string; at: number } | null = null;

// --- Public API ---

/** Enrich and queue an event. Triggers immediate flush if queue is full. */
export function trackEvent(input: TrackEventInput): void {
  try {
    // Dedup consecutive page_view to same path within 2s
    if (input.event_type === 'page_view' && input.page_path) {
      const now = Date.now();
      if (
        lastPageView &&
        lastPageView.path === input.page_path &&
        now - lastPageView.at < DEDUP_WINDOW_MS
      ) {
        return;
      }
      lastPageView = { path: input.page_path, at: now };
    }

    const { currentTenant, username } = useAuthStore.getState();

    const event: ActivityEvent = {
      ...input,
      source: 'zvgnext',
      session_id: getSessionId(),
      timestamp: new Date().toISOString(),
      tenant_id: currentTenant || undefined,
      user_email: username || undefined,
    };

    queue.push(event);

    if (queue.length >= MAX_QUEUE_SIZE) {
      flush();
    }
  } catch {
    // Silent — never break the app
  }
}

/** Start the periodic flush timer. */
export function startTracker(): void {
  if (flushTimer) return;
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
}

/** Stop the timer and flush remaining events. */
export function stopTracker(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush();
}

/** Immediate flush. Use `keepalive: true` for page unload. */
export function flushNow(options?: { keepalive?: boolean }): void {
  flush(options?.keepalive);
}

// --- Internal ---

function flush(keepalive = false): void {
  try {
    const batch = queue.splice(0, MAX_QUEUE_SIZE);
    if (batch.length === 0 && !keepalive) {
      // On normal flush, also try localStorage retry
      retryLocalStorage();
      return;
    }
    if (batch.length === 0) return;

    const { tenantServerUrl, cpToken, currentTenant } = useAuthStore.getState();
    if (!tenantServerUrl || !cpToken) {
      // Not authenticated — save to localStorage for later
      saveToLocalStorage(batch);
      return;
    }

    const url = `${tenantServerUrl}/api/method/zaviago_backend.api.activity_stream.ingest_events`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${cpToken}`,
      'X-Tenant-ID': currentTenant || '',
    };

    const body = JSON.stringify({ events: batch });

    fetch(url, {
      method: 'POST',
      headers,
      credentials: 'omit',
      body,
      keepalive,
    })
      .then((res) => {
        if (res.ok) {
          // On success, also drain localStorage backlog
          if (!keepalive) retryLocalStorage();
        } else {
          saveToLocalStorage(batch);
        }
      })
      .catch(() => {
        saveToLocalStorage(batch);
      });
  } catch {
    // Silent
  }
}

function retryLocalStorage(): void {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;

    const stored: ActivityEvent[] = JSON.parse(raw);
    if (!Array.isArray(stored) || stored.length === 0) {
      localStorage.removeItem(LS_KEY);
      return;
    }

    // Take a batch and leave the rest
    const batch = stored.splice(0, MAX_QUEUE_SIZE);
    if (stored.length === 0) {
      localStorage.removeItem(LS_KEY);
    } else {
      localStorage.setItem(LS_KEY, JSON.stringify(stored));
    }

    const { tenantServerUrl, cpToken, currentTenant } = useAuthStore.getState();
    if (!tenantServerUrl || !cpToken) return;

    const url = `${tenantServerUrl}/api/method/zaviago_backend.api.activity_stream.ingest_events`;
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${cpToken}`,
        'X-Tenant-ID': currentTenant || '',
      },
      credentials: 'omit',
      body: JSON.stringify({ events: batch }),
    }).catch(() => {
      // Re-save on failure
      saveToLocalStorage(batch);
    });
  } catch {
    // Silent
  }
}

function saveToLocalStorage(events: ActivityEvent[]): void {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const existing: ActivityEvent[] = raw ? JSON.parse(raw) : [];
    const combined = [...existing, ...events].slice(-MAX_LOCALSTORAGE_SIZE);
    localStorage.setItem(LS_KEY, JSON.stringify(combined));
  } catch {
    // localStorage full or unavailable — drop silently
  }
}
